import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatMessage } from '../../../../../common/store/chat/types/chat-message';
import { BotChatMessageContainer } from './bot-chat-message-container';
import { BotChatMessageAvatar } from './bot-chat-message-avatar';
import { BotChatMessageCloud } from './bot-chat-message-cloud';
import { useTheme } from 'styled-components';
import { BotChatMessageCloudP } from './bot-chat-message-cloud-p';
import { BotChatMessageWrapper } from './bot-chat-message-wrapper';
import { BotChatMessageFeedback } from '../bot-chat-message-feedback';
import { BotChatMessageTimestamp } from './bot-chat-message-timestamp';
import { useAppDispatch, useAppSelector } from '../../../../../common/store';
import parseMessage from '../../../../utils/parse-message';
import { sendMessageLogSubmission } from '../../../../../common/store/chat/thunks/send-message-log';
import getMessageByVurablizeEvent from '../../../../utils/get-message-by-vurablize-event';
import VurbalizeEvents from '../../../../../common/types/dto/chat/vurbalize-events';
import { hyperLinkClick } from '../../../../../common/store/chat/thunks/hyper-link-click';
import { useNavigateAndHighlight } from '../../../../hooks/use-navigate-and-highlight';
import CurrentFeedback from './current-feedback';

interface UserChatMessageProps {
  message: ChatMessage;
  index: number;
  isLastMessage: boolean;
}

export const BotChatMessage: FC<UserChatMessageProps> = ({
  message,
  index,
  isLastMessage,
}) => {
  const { triggerNavigation } = useNavigateAndHighlight();
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [isShowFeedback, setIsShowFeedback] = useState(false);
  const dispatch  = useAppDispatch();
  const isStreamingHandling = useAppSelector(state => state.chat.isStreamingHandling);
  const isDoneAnimationBuffer = useAppSelector(state => state.chat.isDoneAnimationBuffer);
  const sendMessageLog  = useCallback(() => {
    dispatch(sendMessageLogSubmission({
      data: {
        message: getMessageByVurablizeEvent(VurbalizeEvents.REDIRECT_SUGGESTED),
        event_name: VurbalizeEvents.REDIRECT_SUGGESTED,
        event_data: {
          timestamp: Date.now(),
          messageContent: message.value,
        },
        turn_id: message.turn_id,
      }
    }))
  }, [message])

  const parsedMessage = useMemo(
    () => parseMessage(message.value),
    [message.value],
  );

  // Only log REDIRECT_SUGGESTED when the message is finalized and is the last message
  useEffect(() => {
    if (!isStreamingHandling && isDoneAnimationBuffer && isLastMessage) {
      sendMessageLog();
    }
    // eslint-disable-next-line
  }, [isStreamingHandling, isDoneAnimationBuffer, isLastMessage]);

  const onHyperlinkClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    const { target } = e;
    // @ts-ignore
    const url = target?.dataset['navUrl'];

    if (!url) {
      return;
    }

    dispatch(hyperLinkClick({ url, navigate: triggerNavigation, message }));
  }, [triggerNavigation, message]);

  const avatarSrc = useMemo(() => {
    let src = theme?.themeConfig?.chatMessages?.botMessage?.avatar?.src
    if (message.itemType === 'live_agent') {
      src = message.avatar_link;
    }

    return src;
  }, [message.itemType, message.avatar_link, theme?.themeConfig?.chatMessages?.botMessage?.avatar?.src]);

  const isWaitingAnimationOrResponse = useMemo(
    () => isStreamingHandling || !isDoneAnimationBuffer,
    [isStreamingHandling, isDoneAnimationBuffer],
  );

  const visibleFeedback = useMemo(() => {
    if (message.itemType === 'live_agent') return false

    const isShowForLast = isLastMessage && !isWaitingAnimationOrResponse;
    return isShowFeedback || isShowForLast;
  }, [isLastMessage, isShowFeedback, isWaitingAnimationOrResponse])

  const toggleFeedback = () => setIsShowFeedback(prevValue => !prevValue);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsShowFeedback(false)
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref]);


  const formattedTime = useMemo(() => {
    if (!message.timestamp) return '';
  
    const timestampInSeconds = Number(message.timestamp);
  
    if (!isNaN(timestampInSeconds)) {
      const date = new Date(timestampInSeconds * 1000); // Convert seconds → ms
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
  
    return '';
  }, [message.timestamp]);
  

  return (
    <BotChatMessageWrapper id={isLastMessage ? 'response-last-message' : undefined} ref={ref} onClick={onHyperlinkClick} className="voa_chat_bot_message">
      <BotChatMessageContainer className="voa_avatar_message_container">
        <BotChatMessageAvatar src={avatarSrc} />
        <div onClick={toggleFeedback} className="voa_chat_bot_message_container">
          <BotChatMessageCloud className="voa_chat_bot_message_cloud_container">
            <BotChatMessageCloudP
              dangerouslySetInnerHTML={{ __html: parsedMessage }}
              className="voa_chat_bot_message_text"
            />
          </BotChatMessageCloud>
        </div>
        {(message.mode || message.feedback) && !visibleFeedback &&(
          <CurrentFeedback mode={message.mode} feedback={message.feedback} />
        )}
      </BotChatMessageContainer>
      {formattedTime && (
        <BotChatMessageTimestamp
          timestamp={formattedTime}
          actor={message.itemType}
        />
      )}
      {visibleFeedback &&
        <BotChatMessageFeedback index={index} message={message} />
      }
    </BotChatMessageWrapper>
  );
};
